import { useQuery } from '@tanstack/react-query';
import apiRequest from 'helpers/apiRequest';

/**
 * Fetch global inventory summary across all branches
 */
export const useFetchGlobalInventory = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['owner-inventory-summary'],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'owner/inventory-summary',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loading: isPending,
    inventoryData: data?.data?.data ?? { summary: {}, branches: [] },
    success: isSuccess,
    isError,
    refetch,
  };
};

/**
 * Fetch stock movements across all branches
 */
export const useFetchGlobalStockMovements = (days: number = 7) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['owner-stock-movements', days],
    queryFn: async () => {
      const response = await apiRequest({
        url: `owner/stock-movements?days=${days}`,
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loading: isPending,
    movementsData: data?.data?.data ?? { movements: [], total_count: 0 },
    success: isSuccess,
    isError,
    refetch,
  };
};

/**
 * Fetch branch performance comparison
 */
export const useFetchBranchPerformance = (days: number = 30) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['owner-branch-performance', days],
    queryFn: async () => {
      const response = await apiRequest({
        url: `owner/branch-performance?days=${days}`,
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loading: isPending,
    performanceData: data?.data?.data ?? { summary: {}, branches: [] },
    success: isSuccess,
    isError,
    refetch,
  };
};

/**
 * Fetch current user's branch context
 */
export const useFetchBranchContext = () => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['owner-branch-context'],
    queryFn: async () => {
      const response = await apiRequest({
        url: 'owner/my-branch-context',
        method: 'GET',
      });
      return response;
    },
  });

  return {
    loading: isPending,
    branchContext: data?.data?.data ?? { is_owner: false, is_branch_admin: false, branches: [] },
    success: isSuccess,
    isError,
    refetch,
  };
};

/**
 * Fetch branch settings
 */
export const useFetchBranchSettings = (branchId: number | null) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['owner-branch-settings', branchId],
    queryFn: async () => {
      if (!branchId) return null;
      const response = await apiRequest({
        url: `owner/branch-settings/${branchId}`,
        method: 'GET',
      });
      return response;
    },
    enabled: !!branchId,
  });

  return {
    loading: isPending,
    settings: data?.data?.data ?? null,
    success: isSuccess,
    isError,
    refetch,
  };
};

interface BranchSettingsUpdate {
  stock_management_mode?: 'centralized' | 'independent' | 'hybrid';
  is_self_managing?: boolean;
  can_transfer_stock?: boolean;
  can_set_pricing?: boolean;
  requires_main_approval?: boolean;
}

/**
 * Update branch settings
 */
export const useUpdateBranchSettings = () => {
  const updateSettings = async (branchId: number, settings: BranchSettingsUpdate) => {
    const response = await apiRequest({
      url: `owner/branch-settings/${branchId}`,
      method: 'PUT',
      payload: settings,
    });
    return response;
  };

  return { updateSettings };
};

/**
 * Set a branch as the main branch
 */
export const useSetMainBranch = () => {
  const setMainBranch = async (branchId: number) => {
    const response = await apiRequest({
      url: `owner/set-main-branch/${branchId}`,
      method: 'PUT',
    });
    return response;
  };

  return { setMainBranch };
};
